// The concierge remote stores the last conversation id per dispensary under
// `concierge-conversation-id:<dispensaryId>`. On a shared kiosk that key would
// let the next walk-up resume the previous shopper's conversation, so the lock
// screen — where every kiosk reset path lands — wipes all of them.
//
// The prefix is part of the bridge contract, pinned in both repos.
import { getLogger } from 'src/utils/datadog-logger';
import { CONCIERGE_CONVERSATION_ID_KEY_PREFIX } from './contract';

const logger = getLogger('concierge-storage');

// Mirrors the remote's guest-token key, `concierge-token:<dispensaryId>`
// (dutchie-cf-agents workers/consumer-client/src/utils/conversation-id.ts).
// Not part of the pinned bridge contract, so it lives here, not in ./contract.
export const CONCIERGE_GUEST_TOKEN_KEY_PREFIX = 'concierge-token:';

const CONCIERGE_STORAGE_KEY_PREFIXES = [CONCIERGE_CONVERSATION_ID_KEY_PREFIX, CONCIERGE_GUEST_TOKEN_KEY_PREFIX];

/**
 * Logs a storage failure with the operation and error name only (never key
 * values), at most once per operation per clear call.
 */
function reportFailure(reported: Set<string>, operation: string, error: unknown): void {
  if (reported.has(operation)) return;
  reported.add(operation);
  logger.warn('Concierge storage clear failed', {
    operation,
    errorName: error instanceof Error ? error.name : 'unknown',
  });
}

/**
 * Removes every per-dispensary conversation id and guest token.
 * Best-effort clear. Kiosk browsers can deny storage access entirely, and a
 * thrown SecurityError here must never stop the caller's session reset, so
 * every access is individually guarded.
 */
export function clearConciergeConversationKeys(): void {
  const keys: string[] = [];
  const reported = new Set<string>();
  let length = 0;
  try {
    length = localStorage.length;
  } catch (error) {
    reportFailure(reported, 'length', error);
    /* storage denied — nothing to scan */
  }

  // A failing read of one index must not abort the rest of the scan.
  for (let i = length - 1; i >= 0; i -= 1) {
    try {
      const key = localStorage.key(i);
      if (key && CONCIERGE_STORAGE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        keys.push(key);
      }
    } catch (error) {
      reportFailure(reported, 'key', error);
      /* skip this index */
    }
  }

  keys.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      reportFailure(reported, 'removeItem', error);
      /* storage denied — clearing is defense-in-depth, never blocking */
    }
  });
}

export function readConciergeConversationId(dispensaryId?: string | null): string | null {
  if (!dispensaryId) {
    return null;
  }
  try {
    return localStorage.getItem(`${CONCIERGE_CONVERSATION_ID_KEY_PREFIX}${dispensaryId}`);
  } catch {
    return null;
  }
}
